import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Button, IconButton, Chip, CircularProgress, Collapse,
  MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, LinearProgress, Alert, Tabs, Tab, InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  FaSearch, FaDownload, FaChevronDown, FaChevronUp, FaHistory, FaExclamationTriangle,
  FaArrowLeft, FaBalanceScale, FaFilter, FaTimes, FaCopy,
} from 'react-icons/fa';
import { useSnackbar } from 'notistack';
import { MdWarning } from 'react-icons/md';
import * as XLSX from 'xlsx';
import InterfaceHeader from '../components/InterfaceHeader';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';

const AI_COLOR = TOOL_COLORS.AI.primary;

const SOURCE_FIELD_LABELS = {
  itemId: 'Item ID', webId: 'Web ID', design: 'Design', designName: 'Design Name',
  collection: 'Collection', color: 'Color', size: 'Size', shape: 'Shape',
  style: 'Style', content: 'Content', weave: 'Weave', machineWashable: 'Machine Washable',
  indoorOutdoor: 'Indoor/Outdoor', description: 'Description', productType: 'Product Type',
};

const SEARCH_COLUMNS = [
  { value: '', label: 'Item ID + Title' },
  { value: 'item_id', label: 'Item ID' },
  { value: 'generated_title', label: 'Generated Title' },
  { value: 'collection', label: 'Collection' },
  { value: 'color', label: 'Color' },
  { value: 'design', label: 'Design' },
  { value: 'design_name', label: 'Design Name' },
  { value: 'size', label: 'Size' },
  { value: 'shape', label: 'Shape' },
  { value: 'style', label: 'Style' },
  { value: 'content', label: 'Content' },
  { value: 'weave', label: 'Weave' },
  { value: 'web_id', label: 'Web ID' },
  { value: 'indoor_outdoor', label: 'Indoor/Outdoor' },
  { value: 'product_type', label: 'Product Type' },
];

const AmazonRugTitles = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // View state
  const [view, setView] = useState('runs'); // 'runs', 'detail', 'compare'
  const [mainTab, setMainTab] = useState(0); // 0=Results, 1=Missing Data

  // Runs
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // Selected run detail
  const [selectedRun, setSelectedRun] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);

  // Resizable Generated Title column
  const [titleColWidth, setTitleColWidth] = useState(400);
  const resizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(400);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = titleColWidth;

    const handleResizeMove = (moveE) => {
      if (!resizing.current) return;
      const newWidth = Math.max(150, startWidth.current + (moveE.clientX - startX.current));
      setTitleColWidth(newWidth);
    };

    const handleResizeEnd = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [titleColWidth]);

  const handleResizeDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setTitleColWidth(prev => prev === 400 ? 1200 : 400);
  }, []);

  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [shapeFilter, setShapeFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [hasMissing, setHasMissing] = useState(false);
  const [sortBy, setSortBy] = useState('item_id');
  const [sortDir, setSortDir] = useState('asc');
  const [filterOptions, setFilterOptions] = useState({ collections: [], shapes: [], sizes: [] });

  // Missing data
  const [missingData, setMissingData] = useState(null);
  const [missingLoading, setMissingLoading] = useState(false);

  // Item history dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItemId, setHistoryItemId] = useState('');
  const [itemHistory, setItemHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Compare
  const [compareRunA, setCompareRunA] = useState('');
  const [compareRunB, setCompareRunB] = useState('');
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [comparePage, setComparePage] = useState(0);

  // Export
  const [exporting, setExporting] = useState(false);

  // Fetch runs on mount
  useEffect(() => { fetchRuns(); }, []);

  const fetchRuns = async () => {
    setRunsLoading(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_RUNS);
      setRuns(data.runs || []);
    } catch (err) {
      console.error('Error fetching runs:', err);
    } finally {
      setRunsLoading(false);
    }
  };

  const fetchResults = useCallback(async (runId, pg = 0) => {
    setResultsLoading(true);
    try {
      const params = new URLSearchParams({
        page: (pg + 1).toString(),
        pageSize: rowsPerPage.toString(),
        sortBy,
        sortDir,
      });
      if (search) {
        params.set('search', search);
        if (searchColumn) params.set('searchColumn', searchColumn);
      }
      if (collectionFilter) params.set('collection', collectionFilter);
      if (shapeFilter) params.set('shape', shapeFilter);
      if (sizeFilter) params.set('size', sizeFilter);
      if (hasMissing) params.set('hasMissing', 'true');

      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_RUN_RESULTS(runId, params.toString()));
      setResults(data.results || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching results:', err);
    } finally {
      setResultsLoading(false);
    }
  }, [rowsPerPage, search, searchColumn, collectionFilter, shapeFilter, sizeFilter, hasMissing, sortBy, sortDir]);

  const fetchFilterOptions = useCallback(async (runId, col = '', shp = '') => {
    try {
      const params = new URLSearchParams();
      if (col) params.set('collection', col);
      if (shp) params.set('shape', shp);
      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_FILTERS(runId, params.toString()));
      setFilterOptions(data || { collections: [], shapes: [], sizes: [] });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, []);

  const fetchMissing = async (runId) => {
    setMissingLoading(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_MISSING(runId));
      setMissingData(data);
    } catch (err) {
      console.error('Error fetching missing data:', err);
    } finally {
      setMissingLoading(false);
    }
  };

  const fetchItemHistory = async (itemId) => {
    setHistoryLoading(true);
    setHistoryItemId(itemId);
    setHistoryDialogOpen(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_ITEM_HISTORY(itemId));
      setItemHistory(data.history || []);
    } catch (err) {
      console.error('Error fetching item history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSelectRun = (run) => {
    setSelectedRun(run);
    setView('detail');
    setPage(0);
    setSearch('');
    setSearchInput('');
    setSearchColumn('');
    setCollectionFilter('');
    setShapeFilter('');
    setSizeFilter('');
    setHasMissing(false);
    setSortBy('item_id');
    setSortDir('asc');
    setMainTab(0);
    setExpandedRow(null);
    fetchResults(run.runId, 0);
    fetchFilterOptions(run.runId);
  };

  const handleBackToRuns = () => {
    setView('runs');
    setSelectedRun(null);
    setResults([]);
    setMissingData(null);
  };

  // Refetch when filters/sort/page change
  useEffect(() => {
    if (selectedRun) {
      fetchResults(selectedRun.runId, page);
    }
  }, [selectedRun, page, fetchResults]);

  // Cascading filters: re-fetch filter options when collection or shape changes
  useEffect(() => {
    if (selectedRun) {
      fetchFilterOptions(selectedRun.runId, collectionFilter, shapeFilter);
    }
  }, [selectedRun, collectionFilter, shapeFilter, fetchFilterOptions]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearch('');
    setSearchColumn('');
    setCollectionFilter('');
    setShapeFilter('');
    setSizeFilter('');
    setHasMissing(false);
    setPage(0);
  };

  const handleCopyTitle = (title, itemId) => {
    navigator.clipboard.writeText(title).then(() => {
      enqueueSnackbar(`Title copied for ${itemId}`, { variant: 'success', autoHideDuration: 2000 });
    }).catch(() => {
      enqueueSnackbar('Failed to copy', { variant: 'error', autoHideDuration: 2000 });
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Tab change for missing data
  useEffect(() => {
    if (mainTab === 1 && selectedRun && !missingData) {
      fetchMissing(selectedRun.runId);
    }
  }, [mainTab, selectedRun, missingData]);

  // Export to Excel
  const handleExport = async () => {
    if (!selectedRun) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (collectionFilter) params.set('collection', collectionFilter);
      if (shapeFilter) params.set('shape', shapeFilter);
      if (sizeFilter) params.set('size', sizeFilter);
      if (hasMissing) params.set('hasMissing', 'true');

      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_EXPORT(selectedRun.runId, params.toString()));
      const rows = (data.rows || []).map(r => ({
        'Item ID': r.itemId,
        'Generated Title': r.generatedTitle,
        'Title Length': r.titleLength,
        'Web ID': r.webId,
        'Design': r.design,
        'Design Name': r.designName,
        'Collection': r.collection,
        'Color': r.color,
        'Size': r.size,
        'Shape': r.shape,
        'Style': r.style,
        'Content': r.content,
        'Weave': r.weave,
        'Machine Washable': r.machineWashable,
        'Indoor/Outdoor': r.indoorOutdoor,
        'Description': r.description,
        'Product Type': r.productType,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 80 }, { wch: 12 }, { wch: 22 }, { wch: 12 }, { wch: 20 },
        { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 30 },
        { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 40 }, { wch: 18 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Rug Titles');
      const dt = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      XLSX.writeFile(wb, `Amazon_Rug_Titles_Run${selectedRun.runId}_${dt}.xlsx`);
    } catch (err) {
      console.error('Error exporting:', err);
    } finally {
      setExporting(false);
    }
  };

  // Compare runs
  const handleCompare = async () => {
    if (!compareRunA || !compareRunB) return;
    setCompareLoading(true);
    setView('compare');
    setComparePage(0);
    try {
      const params = new URLSearchParams({
        runA: compareRunA,
        runB: compareRunB,
        onlyChanged: 'true',
        page: '1',
        pageSize: '50',
      });
      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_COMPARE(params.toString()));
      setCompareData(data);
    } catch (err) {
      console.error('Error comparing runs:', err);
    } finally {
      setCompareLoading(false);
    }
  };

  const fetchComparePage = async (pg) => {
    if (!compareRunA || !compareRunB) return;
    setCompareLoading(true);
    try {
      const params = new URLSearchParams({
        runA: compareRunA,
        runB: compareRunB,
        onlyChanged: 'true',
        page: (pg + 1).toString(),
        pageSize: '50',
      });
      const data = await apiCall(API_ENDPOINTS.AMAZON_RUG_TITLES_COMPARE(params.toString()));
      setCompareData(data);
      setComparePage(pg);
    } catch (err) {
      console.error('Error comparing runs:', err);
    } finally {
      setCompareLoading(false);
    }
  };

  const SortableHeader = ({ column, label }) => (
    <TableCell
      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => handleSort(column)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {sortBy === column && (
          <Typography variant="caption" sx={{ ml: 0.5 }}>
            {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
          </Typography>
        )}
      </Box>
    </TableCell>
  );

  // ==================== RUNS LIST VIEW ====================
  const renderRunsList = () => (
    <Box>
      {/* Compare controls */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FaBalanceScale size={18} color={AI_COLOR} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Compare Runs</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Run A</InputLabel>
            <Select value={compareRunA} onChange={(e) => setCompareRunA(e.target.value)} label="Run A">
              {runs.map(r => (
                <MenuItem key={r.runId} value={r.runId}>{r.runDate} (#{r.runId})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2">vs</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Run B</InputLabel>
            <Select value={compareRunB} onChange={(e) => setCompareRunB(e.target.value)} label="Run B">
              {runs.map(r => (
                <MenuItem key={r.runId} value={r.runId}>{r.runDate} (#{r.runId})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            disabled={!compareRunA || !compareRunB || compareRunA === compareRunB}
            onClick={handleCompare}
            sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}
          >
            Compare
          </Button>
        </Box>
      </Paper>

      {/* Runs table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {runsLoading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }} />}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Run ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Spec Version</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Total Items</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Errors</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Over 200</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Avg Length</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Max Length</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source File</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow
                key={run.runId}
                hover
                onClick={() => handleSelectRun(run)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: `${AI_COLOR}08` } }}
              >
                <TableCell>
                  <Chip label={`#${run.runId}`} size="small" sx={{ bgcolor: `${AI_COLOR}15`, fontWeight: 600 }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{run.runDate}</TableCell>
                <TableCell>
                  <Chip label={run.specVersion} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">{(run.totalItems || 0).toLocaleString()}</TableCell>
                <TableCell align="right">
                  {run.errorCount > 0 ? (
                    <Chip label={run.errorCount} size="small" color="error" />
                  ) : (
                    <Typography variant="body2" color="success.main">0</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {run.over200Count > 0 ? (
                    <Chip label={run.over200Count} size="small" color="warning" />
                  ) : (
                    <Typography variant="body2" color="success.main">0</Typography>
                  )}
                </TableCell>
                <TableCell align="right">{run.avgTitleLength ?? '-'}</TableCell>
                <TableCell align="right">{run.maxTitleLength ?? '-'}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {run.sourceFile || '-'}
                </TableCell>
                <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={run.notes || ''}>
                    <span>{run.notes || '-'}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!runsLoading && runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No runs found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ==================== RUN DETAIL VIEW ====================
  const renderRunDetail = () => (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={handleBackToRuns} size="small">
          <FaArrowLeft />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Run #{selectedRun.runId} - {selectedRun.runDate}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
            <Chip label={selectedRun.specVersion} size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              {(selectedRun.totalItems || 0).toLocaleString()} items
            </Typography>
            {selectedRun.errorCount > 0 && (
              <Chip label={`${selectedRun.errorCount} errors`} size="small" color="error" />
            )}
            {selectedRun.sourceFile && (
              <Typography variant="body2" color="text.secondary">
                Source: {selectedRun.sourceFile}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={exporting ? <CircularProgress size={16} /> : <FaDownload size={14} />}
          disabled={exporting}
          onClick={handleExport}
          sx={{ borderColor: AI_COLOR, color: AI_COLOR, '&:hover': { borderColor: AI_COLOR, bgcolor: `${AI_COLOR}08` } }}
        >
          {exporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={mainTab}
        onChange={(e, v) => setMainTab(v)}
        sx={{ mb: 2, '& .MuiTab-root.Mui-selected': { color: AI_COLOR }, '& .MuiTabs-indicator': { bgcolor: AI_COLOR } }}
      >
        <Tab label="Results" />
        <Tab label="Missing Data" />
      </Tabs>

      {mainTab === 0 && renderResultsTab()}
      {mainTab === 1 && renderMissingTab()}
    </Box>
  );

  // ==================== RESULTS TAB ====================
  const renderResultsTab = () => (
    <Box>
      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Search In</InputLabel>
            <Select value={searchColumn} onChange={(e) => setSearchColumn(e.target.value)} label="Search In">
              {SEARCH_COLUMNS.map(col => (
                <MenuItem key={col.value} value={col.value}>{col.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder={searchColumn ? `Search in ${SEARCH_COLUMNS.find(c => c.value === searchColumn)?.label}...` : 'Search Item ID + Title...'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch size={14} color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
          />
          <Button size="small" onClick={handleSearch} variant="contained"
            sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
            Search
          </Button>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Collection</InputLabel>
            <Select value={collectionFilter} onChange={(e) => { setCollectionFilter(e.target.value); setShapeFilter(''); setSizeFilter(''); setPage(0); }} label="Collection">
              <MenuItem value="">All</MenuItem>
              {filterOptions.collections.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Shape</InputLabel>
            <Select value={shapeFilter} onChange={(e) => { setShapeFilter(e.target.value); setSizeFilter(''); setPage(0); }} label="Shape">
              <MenuItem value="">All</MenuItem>
              {filterOptions.shapes.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Size</InputLabel>
            <Select value={sizeFilter} onChange={(e) => { setSizeFilter(e.target.value); setPage(0); }} label="Size">
              <MenuItem value="">All</MenuItem>
              {filterOptions.sizes.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={hasMissing}
                onChange={(e) => { setHasMissing(e.target.checked); setPage(0); }}
                size="small"
                sx={{ '& .Mui-checked': { color: AI_COLOR }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }}
              />
            }
            label={<Typography variant="body2">Missing Only</Typography>}
          />

          {(search || searchColumn || collectionFilter || shapeFilter || sizeFilter || hasMissing) && (
            <Button size="small" startIcon={<FaTimes size={12} />} onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Results table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {resultsLoading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }} />}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
              <TableCell sx={{ width: 40 }} />
              <SortableHeader column="item_id" label="Item ID" />
              <TableCell
                sx={{ fontWeight: 600, width: titleColWidth, minWidth: 150, position: 'relative', userSelect: 'none' }}
                onClick={() => handleSort('generated_title')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  Generated Title
                  {sortBy === 'generated_title' && (
                    <span style={{ marginLeft: 4, fontSize: 12 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </Box>
                <Box
                  onMouseDown={handleResizeStart}
                  onDoubleClick={handleResizeDoubleClick}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '15%',
                    bottom: '15%',
                    width: 5,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : `${AI_COLOR}40`,
                    cursor: 'col-resize',
                    '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : AI_COLOR, width: 6 },
                    transition: 'background-color 0.2s, width 0.2s',
                    zIndex: 1,
                  }}
                />
              </TableCell>
              <SortableHeader column="title_length" label="Length" />
              <SortableHeader column="src_collection" label="Collection" />
              <SortableHeader column="src_shape" label="Shape" />
              <SortableHeader column="src_color" label="Color" />
              <TableCell sx={{ fontWeight: 600 }}>Missing</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  hover
                  sx={{ '&:hover': { bgcolor: `${AI_COLOR}04` }, cursor: 'pointer' }}
                  onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedRow === row.id ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {row.itemId}
                  </TableCell>
                  <TableCell sx={{ maxWidth: titleColWidth, width: titleColWidth }}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-word',
                        ...(expandedRow !== row.id && {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }),
                      }}
                    >
                      {row.generatedTitle}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.titleLength}
                      size="small"
                      sx={{
                        bgcolor: row.titleLength > 200 ? 'error.main' : row.titleLength > 180 ? 'warning.main' : 'success.main',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.sourceData?.collection === 'MISSING' ? <MissingBadge /> : row.sourceData?.collection}</TableCell>
                  <TableCell>{row.sourceData?.shape === 'MISSING' ? <MissingBadge /> : row.sourceData?.shape}</TableCell>
                  <TableCell>{row.sourceData?.color === 'MISSING' ? <MissingBadge /> : row.sourceData?.color}</TableCell>
                  <TableCell>
                    {(() => {
                      const filtered = (row.missingFields || []).filter(f => f !== 'indoorOutdoor');
                      return filtered.length > 0 ? (
                        <Chip
                          icon={<FaExclamationTriangle size={10} />}
                          label={filtered.length}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : (
                        <Typography variant="caption" color="success.main">None</Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Copy Title">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleCopyTitle(row.generatedTitle, row.itemId); }}
                          sx={{ color: theme.palette.text.secondary, '&:hover': { color: AI_COLOR } }}
                        >
                          <FaCopy size={13} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View History">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); fetchItemHistory(row.itemId); }}
                          sx={{ color: AI_COLOR }}
                        >
                          <FaHistory size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Expanded row - source data */}
                <TableRow>
                  <TableCell sx={{ py: 0, borderBottom: expandedRow === row.id ? undefined : 'none' }} colSpan={9}>
                    <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Source Data
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
                          {row.sourceData && Object.entries(row.sourceData).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                                {SOURCE_FIELD_LABELS[key] || key}:
                              </Typography>
                              {value === 'MISSING' ? (
                                <MissingBadge />
                              ) : (
                                <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>
                                  {value}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Full Title
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {row.generatedTitle}
                            </Typography>
                          </Paper>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {!resultsLoading && results.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No results found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>
    </Box>
  );

  // ==================== MISSING DATA TAB ====================
  const renderMissingTab = () => {
    if (missingLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: AI_COLOR }} />
        </Box>
      );
    }
    if (!missingData) return null;

    const { totalItems, missingCounts } = missingData;
    const sortedMissing = Object.entries(missingCounts || {}).sort((a, b) => b[1] - a[1]);

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Missing data summary for {(totalItems || 0).toLocaleString()} items. Fields stored as "MISSING" indicate data
          that was unavailable at generation time.
        </Alert>

        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Source Field</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Missing Count</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Missing %</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Coverage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMissing.map(([field, count]) => {
                const pct = totalItems > 0 ? ((count / totalItems) * 100) : 0;
                const coveragePct = 100 - pct;
                return (
                  <TableRow key={field}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {SOURCE_FIELD_LABELS[field] || field}
                    </TableCell>
                    <TableCell align="right">{count.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${pct.toFixed(1)}%`}
                        size="small"
                        sx={{
                          bgcolor: pct > 50 ? 'error.main' : pct > 10 ? 'warning.main' : 'success.main',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, height: 8, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{
                            height: '100%',
                            width: `${coveragePct}%`,
                            bgcolor: coveragePct > 90 ? 'success.main' : coveragePct > 50 ? 'warning.main' : 'error.main',
                            borderRadius: 4,
                            transition: 'width 0.5s ease',
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 40 }}>
                          {coveragePct.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ==================== COMPARE VIEW ====================
  const renderCompare = () => {
    if (compareLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: AI_COLOR }} />
        </Box>
      );
    }
    if (!compareData) return null;

    const { runA, runB, summary, differences } = compareData;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => { setView('runs'); setCompareData(null); }} size="small">
            <FaArrowLeft />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Compare: Run #{runA?.runId} ({runA?.runDate}) vs Run #{runB?.runId} ({runB?.runDate})
          </Typography>
        </Box>

        {/* Summary cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
          {[
            { label: 'Items in A', value: summary?.totalInA?.toLocaleString() },
            { label: 'Items in B', value: summary?.totalInB?.toLocaleString() },
            { label: 'In Both', value: summary?.itemsInBoth?.toLocaleString() },
            { label: 'Changed', value: summary?.changedTitles?.toLocaleString(), color: 'warning.main' },
            { label: 'New in B', value: summary?.newInB?.toLocaleString(), color: 'success.main' },
            { label: 'Dropped', value: summary?.droppedFromA?.toLocaleString(), color: 'error.main' },
          ].map((card) => (
            <Paper key={card.label} elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{card.label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: card.color || 'text.primary' }}>
                {card.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Differences table */}
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Item ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title (Run A)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Len A</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title (Run B)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Len B</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(differences || []).map((diff, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {diff.itemId}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, fontSize: '0.8rem' }}>
                    <Tooltip title={diff.titleA}><span>{diff.titleA}</span></Tooltip>
                  </TableCell>
                  <TableCell align="center">{diff.lengthA}</TableCell>
                  <TableCell sx={{ maxWidth: 300, fontSize: '0.8rem' }}>
                    <Tooltip title={diff.titleB}><span>{diff.titleB}</span></Tooltip>
                  </TableCell>
                  <TableCell align="center">{diff.lengthB}</TableCell>
                </TableRow>
              ))}
              {differences?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No differences found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {summary?.changedTitles > 0 && (
            <TablePagination
              component="div"
              count={summary.changedTitles}
              page={comparePage}
              onPageChange={(e, newPage) => fetchComparePage(newPage)}
              rowsPerPage={50}
              rowsPerPageOptions={[50]}
            />
          )}
        </TableContainer>
      </Box>
    );
  };

  // ==================== ITEM HISTORY DIALOG ====================
  const renderHistoryDialog = () => (
    <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Item History: {historyItemId}
      </DialogTitle>
      <DialogContent>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: AI_COLOR }} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Run ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Spec</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Generated Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Length</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemHistory.map((h, i) => (
                <TableRow key={i} sx={{ bgcolor: h.changed ? `${AI_COLOR}08` : 'transparent' }}>
                  <TableCell>#{h.runId}</TableCell>
                  <TableCell>{h.runDate}</TableCell>
                  <TableCell><Chip label={h.specVersion} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ maxWidth: 400, fontSize: '0.8rem', wordBreak: 'break-word' }}>
                    {h.generatedTitle}
                  </TableCell>
                  <TableCell align="center">{h.titleLength}</TableCell>
                  <TableCell align="center">
                    {h.changed ? (
                      <Chip label="Changed" size="small" color="warning" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {itemHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No history found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <InterfaceHeader title="Amazon Rug Titles" color={AI_COLOR} category="ai" showFavorite={true} />

      <Box sx={{ mt: 2 }}>
        {view === 'runs' && renderRunsList()}
        {view === 'detail' && selectedRun && renderRunDetail()}
        {view === 'compare' && renderCompare()}
      </Box>

      {renderHistoryDialog()}
    </Box>
  );
};

const MissingBadge = () => (
  <Chip
    icon={<MdWarning size={12} />}
    label="MISSING"
    size="small"
    sx={{
      bgcolor: 'error.main',
      color: 'white',
      fontWeight: 600,
      fontSize: '0.65rem',
      height: 22,
    }}
  />
);

export default AmazonRugTitles;
